import React from 'react';
import HeaderOne from './components/NavBar';
import Footer from './components/Footer';
import { CheckIcon } from '@heroicons/react/20/solid';
import Scroll from './scrollToTop';

const tiers = [
  {
    name: 'Family Access',
    id: 'tier-family',
    href: '/pa-signup',
    priceMonthly: '£7',
    priceYearly: '£70',
    description: "Perfect for parents and carers.",
    features: [
      'Access to a map of services across the UK',
      'Use your local authority funding to easily book through our system',
      'Communicate directly with providers through our messaging system',
      'Access a range of SEND tools, support aids, and guidance',
      'Leave reviews on products and services',
      'Regular blogs on all things SEND',
      'Webinars and videos on hot topics',
      "Personalised support based on your child's age and needs",
      'Exclusive discounts and offers'
    ],
    featured: true,
  },
  {
    name: 'Business Access',
    id: 'tier-business',
    href: '/business-signup',
    priceYearly: '£240',
    priceTwoYear: '£400',
    description: 'Ideal for service providers.',
    features: [
      'Receive and manage bookings through our in-house system',
      'Receive payments directly for your services',
      'Advertise on our map',
      'Communicate directly with parents/carers with ease',
      'Access a range of SEND tools, support aids, and guidance',
      'Regular blogs on all things SEND',
      'Webinars and videos on hot topics',
      'Consultancy services available',
      'Training and workshops'
    ],
    featured: true,
    contactInfo: 'Contact us for specialist consultancy support – customerservices@enablesend.co.uk',
  },
  {
    name: 'Local Authority',
    href: '/la-signup',

    id: 'tier-authority',
    description: 'Comprehensive solutions for local authorities.',
    features: [
      'Oversee your SEND support packages',
      'Access to a map of services across the UK',
      'Communicate directly with parents/carers and services',
      'Access a range of SEND tools, support aids, and guidance',
      'Regular blogs on all things SEND',
      'Webinars and videos on hot topics',
      'Consultancy services available',
      'Training and workshops',
      'Ability to report on your users and services purchased'
    ],
    contactInfo: 'Contact us for more information – customerservices@enablesend.co.uk',
    // learnMoreLink: '/',
    featured: true,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Pricing() {
  return (
    <div className="bg-gray-50 min-h-screen">
      <Scroll />
      <HeaderOne />
      <div className="relative isolate px-6 py-24 sm:py-32 lg:px-8">
        <div aria-hidden="true" className="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden blur-3xl">
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-base font-semibold leading-7 text-blue-600">Pricing</h2>
          <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Choose the right plan for you
          </p>
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600">
          Affordable plans packed with features to support your SEND journey, whether you're a family, provider, or local authority.
        </p>
        <div className="mx-auto mt-16 grid max-w-lg gap-8 lg:max-w-7xl lg:grid-cols-3">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={classNames(
                tier.featured ? 'ring-2 ring-blue-600' : 'ring-1 ring-gray-200',
                'flex flex-col justify-between rounded-3xl bg-white p-8 shadow-xl'
              )}
            >
              <div>
                <h3
                  id={tier.id}
                  className={classNames(
                    tier.featured ? 'text-blue-600' : 'text-gray-900',
                    'text-2xl font-bold leading-8'
                  )}
                >
                  {tier.name}
                </h3>
                {tier.priceMonthly && (
                  <p className="mt-6 flex items-baseline gap-x-1">
                    <span className="text-4xl font-bold tracking-tight text-gray-900">{tier.priceMonthly}</span>
                    <span className="text-sm font-semibold leading-6 text-gray-600">/month</span>
                  </p>
                )}
                {tier.priceYearly && !tier.priceMonthly && (
                  <p className="mt-6 flex items-baseline gap-x-1">
                    <span className="text-4xl font-bold tracking-tight text-gray-900">{tier.priceYearly}</span>
                    <span className="text-sm font-semibold leading-6 text-gray-600">/year</span>
                  </p>
                )}
                {tier.priceYearly && tier.priceMonthly && (
                  <p className="mt-1 flex items-baseline gap-x-1">
                    <span className="text-2xl font-semibold tracking-tight text-gray-900">{tier.priceYearly}</span>
                    <span className="text-sm font-semibold leading-6 text-gray-600">/year</span>
                  </p>
                )}
                {tier.priceTwoYear && (
                  <p className="mt-1 flex items-baseline gap-x-1">
                    <span className="text-2xl font-semibold tracking-tight text-gray-900">{tier.priceTwoYear}</span>
                    <span className="text-sm font-semibold leading-6 text-gray-600">/2 years</span>
                  </p>
                )}
                <p className="mt-4 text-sm leading-6 text-gray-600">{tier.description}</p>
                <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-gray-600">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex gap-x-3">
                      <CheckIcon className="h-6 w-5 flex-none text-blue-600" aria-hidden="true" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
         
                <div className="mt-8">
                  <p className="text-sm text-gray-600">{tier.contactInfo}</p>
                  {tier.learnMoreLink && (
                    <a
                      href={tier.learnMoreLink}
                      className="mt-6 block rounded-md bg-blue-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    >
                      See how we can support you
                    </a>
                  )}
                </div>
                <a
                  href={tier.href}
                  className={classNames(
                    tier.featured
                      ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-500'
                      : 'text-blue-600 ring-1 ring-inset ring-blue-200 hover:ring-blue-300',
                    'mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                  )}
                >
                  Get started today
                </a>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}